import { Link } from 'react-router-dom';

const Section = ({ id, icon, title, children }) => (
  <section id={id} className="card border-0 shadow-sm rounded-4 p-4 mb-4">
    <h4 className="fw-bold mb-3 d-flex align-items-center gap-2">
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle flex-shrink-0"
        style={{ width: 40, height: 40 }}
      >
        <i className={`bi ${icon} text-primary`} style={{ fontSize: 18 }}></i>
      </span>
      {title}
    </h4>
    {children}
  </section>
);

const Rule = ({ label, value, highlight }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-muted" style={{ fontSize: 14 }}>
      {label}
    </span>
    <span className={`fw-semibold ${highlight ? 'text-primary' : ''}`} style={{ fontSize: 14 }}>
      {value}
    </span>
  </div>
);

export default function BusinessRulesPage() {
  return (
    <div className="bg-body-tertiary min-vh-100 py-5">
      <div className="container" style={{ maxWidth: 860 }}>
        {/* Header */}
        <div className="text-center mb-5">
          <span
            className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 mb-3"
            style={{ fontSize: 13 }}
          >
            Documentation officielle
          </span>
          <h1 className="fw-bold mb-2">Règles business Moovicar</h1>
          <p className="text-muted" style={{ maxWidth: 580, margin: '0 auto' }}>
            Toutes les règles qui régissent les locations sur la plateforme : commissions,
            annulations, dépôts de garantie, conditions conducteur, kilométrage et carburant.
          </p>
        </div>

        {/* Sommaire */}
        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white">
          <p
            className="fw-semibold mb-3 small text-muted text-uppercase"
            style={{ letterSpacing: 1 }}
          >
            Sommaire
          </p>
          <div className="row g-2">
            {[
              { href: '#commission', icon: 'bi-percent', label: 'Commission plateforme' },
              { href: '#annulation', icon: 'bi-x-circle', label: "Politique d'annulation" },
              { href: '#garantie', icon: 'bi-shield-check', label: 'Dépôt de garantie' },
              { href: '#conducteur', icon: 'bi-person-badge', label: 'Conditions conducteur' },
              { href: '#kilometrage', icon: 'bi-speedometer2', label: 'Kilométrage & carburant' },
            ].map(({ href, icon, label }) => (
              <div key={href} className="col-12 col-sm-6">
                <a
                  href={href}
                  className="d-flex align-items-center gap-2 text-decoration-none text-body p-2 rounded-3 hover-bg"
                  style={{ fontSize: 14 }}
                >
                  <i className={`bi ${icon} text-primary`}></i>
                  {label}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* 1. Commission */}
        <Section id="commission" icon="bi-percent" title="Commission plateforme">
          <div className="alert alert-primary d-flex gap-3 mb-3" style={{ fontSize: 14 }}>
            <i className="bi bi-info-circle-fill flex-shrink-0 mt-1"></i>
            <span>
              Moovicar prélève une commission sur chaque transaction. Elle couvre l'assurance, le
              support, la vérification des profils et le maintien de la plateforme.
            </span>
          </div>
          <Rule label="Commission propriétaire (hôte)" value="15 % du prix de location" highlight />
          <Rule label="Commission conducteur (invité)" value="10 % du prix de location" />
          <Rule label="Commission totale maximale" value="25 %" />
          <Rule label="TVA sur commission" value="20 % (incluse dans la commission)" />
          <div className="mt-3 rounded-3 p-3 bg-body-tertiary" style={{ fontSize: 13 }}>
            <strong>Exemple :</strong> location à 100 €/jour × 3 jours = 300 € brut. Le propriétaire
            perçoit <strong>255 €</strong> (–15 %), le conducteur paie <strong>330 €</strong> (+10
            %).
          </div>
        </Section>

        {/* 2. Annulation */}
        <Section id="annulation" icon="bi-x-circle" title="Politique d'annulation">
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-3" style={{ fontSize: 14 }}>
              <thead className="table-light">
                <tr>
                  <th>Délai avant le début de la location</th>
                  <th>Remboursement conducteur</th>
                  <th>Versement hôte</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <span className="badge bg-success-subtle text-success">{'>'} 7 jours</span>
                  </td>
                  <td>100 %</td>
                  <td>0 %</td>
                </tr>
                <tr>
                  <td>
                    <span className="badge bg-warning-subtle text-warning">48 h – 7 jours</span>
                  </td>
                  <td>50 %</td>
                  <td>50 % (–commission)</td>
                </tr>
                <tr>
                  <td>
                    <span className="badge bg-danger-subtle text-danger">{'<'} 48 h</span>
                  </td>
                  <td>0 %</td>
                  <td>75 % (–commission)</td>
                </tr>
                <tr>
                  <td>
                    <span className="badge bg-danger-subtle text-danger">Annulation hôte</span>
                  </td>
                  <td>100 % + 25 € d'indemnité</td>
                  <td>Pénalité appliquée</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="alert alert-warning d-flex gap-2 mb-0" style={{ fontSize: 13 }}>
            <i className="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"></i>
            <span>
              En cas de <strong>no-show conducteur</strong> (non présentation sans annulation),
              aucun remboursement n'est accordé. Les frais de service ne sont jamais remboursés.
            </span>
          </div>
        </Section>

        {/* 3. Garantie */}
        <Section id="garantie" icon="bi-shield-check" title="Dépôt de garantie">
          <Rule label="Mode de prélèvement" value="Pré-autorisation carte bancaire" />
          <Rule
            label="Délai de libération (location sans incident)"
            value="72 h après checkout validé"
          />
          <Rule label="Délai de libération (litige ouvert)" value="Jusqu'à résolution du litige" />
          <div className="mt-3">
            <p className="fw-semibold mb-2" style={{ fontSize: 14 }}>
              Montants par catégorie de véhicule
            </p>
            <div className="table-responsive">
              <table
                className="table table-sm table-bordered align-middle mb-0"
                style={{ fontSize: 13 }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Catégorie</th>
                    <th>Dépôt de garantie</th>
                    <th>Franchise dommages</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Éco</td>
                    <td>300 €</td>
                    <td>150 €</td>
                  </tr>
                  <tr>
                    <td>Confort</td>
                    <td>600 €</td>
                    <td>300 €</td>
                  </tr>
                  <tr>
                    <td>Privilège / Premium</td>
                    <td>1 200 €</td>
                    <td>600 €</td>
                  </tr>
                  <tr>
                    <td>Prestige</td>
                    <td>2 500 €</td>
                    <td>1 000 €</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="alert alert-info mt-3 mb-0 d-flex gap-2" style={{ fontSize: 13 }}>
            <i className="bi bi-info-circle flex-shrink-0 mt-1"></i>
            <span>
              La garantie est une <strong>pré-autorisation</strong>, pas un débit réel. Elle n'est
              capturée qu'en cas de dommages constatés à l'état des lieux de retour.
            </span>
          </div>
        </Section>

        {/* 4. Conducteur */}
        <Section id="conducteur" icon="bi-person-badge" title="Conditions conducteur">
          <Rule label="Âge minimum (catégorie Éco)" value="21 ans" />
          <Rule label="Âge minimum (catégorie Confort+)" value="25 ans" />
          <Rule label="Ancienneté permis minimum (Éco)" value="2 ans" />
          <Rule label="Ancienneté permis minimum (Prestige)" value="10 ans" />
          <Rule label="Pièce d'identité" value="Obligatoire (uploadée + vérifiée)" />
          <Rule label="Permis de conduire" value="Original requis au check-in" />
          <div className="mt-3">
            <p className="fw-semibold mb-2" style={{ fontSize: 14 }}>
              Supplément jeune conducteur
            </p>
            <div className="table-responsive">
              <table
                className="table table-sm table-bordered align-middle mb-0"
                style={{ fontSize: 13 }}
              >
                <thead className="table-light">
                  <tr>
                    <th>Tranche d'âge</th>
                    <th>Supplément/jour</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>18 – 21 ans</td>
                    <td>8 € / jour</td>
                  </tr>
                  <tr>
                    <td>22 – 25 ans</td>
                    <td>4 € / jour</td>
                  </tr>
                  <tr>
                    <td>26 ans et +</td>
                    <td>0 € / jour</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-3">
            <Link to="/rental-conditions" className="btn btn-outline-primary btn-sm rounded-3">
              <i className="bi bi-arrow-right me-1"></i>Voir les conditions complètes
            </Link>
          </div>
        </Section>

        {/* 5. Kilométrage & carburant */}
        <Section id="kilometrage" icon="bi-speedometer2" title="Kilométrage & carburant">
          <Rule label="Kilométrage inclus" value="Défini par le propriétaire (ex. 200 km/jour)" />
          <Rule label="Prix km supplémentaire" value="0,13 € / km (par défaut)" highlight />
          <Rule
            label="Prélèvement km supplémentaire"
            value="Automatique au checkout validé (Stripe off-session)"
          />
          <Rule label="Carburant" value="Rendu avec le même niveau qu'au départ" />
          <Rule
            label="Carburant manquant"
            value="Facturation au tarif pompiste + 10 € de frais de service"
          />
          <div className="mt-3 rounded-3 p-3 bg-body-tertiary" style={{ fontSize: 13 }}>
            <strong>Exemple km supplémentaire :</strong> forfait 300 km inclus, conducteur a
            parcouru 420 km → 120 km × 0,13 € = <strong>15,60 €</strong> prélevés automatiquement.
          </div>
          <div className="mt-3 alert alert-warning d-flex gap-2 mb-0" style={{ fontSize: 13 }}>
            <i className="bi bi-camera flex-shrink-0 mt-1"></i>
            <span>
              Les compteurs kilométriques sont photographiés obligatoirement au check-in et au
              checkout pour garantir une facturation transparente.
            </span>
          </div>
        </Section>

        {/* Footer nav */}
        <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
          <Link to="/comment-ca-marche" className="btn btn-outline-primary rounded-3 btn-sm">
            <i className="bi bi-map me-1"></i>Voir les user flows
          </Link>
          <Link to="/etats-reservation" className="btn btn-outline-secondary rounded-3 btn-sm">
            <i className="bi bi-diagram-3 me-1"></i>États de réservation
          </Link>
          <Link to="/rental-conditions" className="btn btn-outline-secondary rounded-3 btn-sm">
            <i className="bi bi-file-text me-1"></i>Conditions de location
          </Link>
        </div>
      </div>
    </div>
  );
}
